import React, { useEffect } from 'react';
import { executeScriptSecurely } from './secureWeb3Provider';
import { getNonce } from './nonce';

/**
 * Helper function to securely add meta tags to the document head
 * 
 * @param metaTags Array of meta tag objects with name/property and content
 */
export const addMetaTags = (metaTags: Array<{
  type: 'name' | 'property' | 'itemProp';
  name: string;
  content: string;
}>) => {
  metaTags.forEach(tag => {
    // Check if meta tag already exists
    const existingTag = document.querySelector(
      `meta[${tag.type}="${tag.name}"]`
    );
    
    if (!existingTag) {
      const meta = document.createElement('meta');
      meta.setAttribute(tag.type, tag.name);
      meta.setAttribute('content', tag.content);
      document.head.appendChild(meta);
    }
  });
};

/**
 * Helper function to securely add structured data
 * 
 * @param structuredData Object containing structured data
 */
export const addStructuredData = (structuredData: any) => {
  const stringifiedData = JSON.stringify(structuredData);
  
  executeScriptSecurely(`
    var schemaScript = document.createElement('script');
    schemaScript.type = 'application/ld+json';
    schemaScript.textContent = ${JSON.stringify(stringifiedData)};
    document.head.appendChild(schemaScript);
  `);
};

/**
 * Helper function to securely add link tags
 * 
 * @param linkTags Array of link tag objects
 */
export const addLinkTags = (linkTags: Array<{
  rel: string;
  href: string;
  [key: string]: string;
}>) => {
  linkTags.forEach(tag => {
    // Check if link tag already exists
    const selector = `link[rel="${tag.rel}"]${tag.href ? `[href="${tag.href}"]` : ''}`;
    const existingLink = document.querySelector(selector);
    
    if (!existingLink) {
      const link = document.createElement('link');
      
      // Add all attributes
      Object.keys(tag).forEach(attr => {
        link.setAttribute(attr, tag[attr]);
      });
      
      document.head.appendChild(link);
    }
  });
};

/**
 * Helper function to set the document title
 * 
 * @param title Document title
 */
export const setDocumentTitle = (title: string) => {
  document.title = title;
};

/**
 * Secure SEO component that replaces react-helmet with direct DOM manipulation
 */
export const SecureSEO: React.FC<{
  title?: string;
  metaTags?: Array<{ type: 'name' | 'property' | 'itemProp'; name: string; content: string }>;
  structuredData?: any;
  linkTags?: Array<{ rel: string; href: string; [key: string]: string }>;
}> = ({ title, metaTags, structuredData, linkTags }) => {
  useEffect(() => {
    // Set document title if provided
    if (title) {
      setDocumentTitle(title);
    }
    
    // Add meta tags if provided
    if (metaTags && metaTags.length > 0) {
      addMetaTags(metaTags);
    }
    
    // Add structured data if provided
    if (structuredData) {
      addStructuredData(structuredData);
    }
    
    // Add link tags if provided
    if (linkTags && linkTags.length > 0) {
      addLinkTags(linkTags);
    }
  }, [title, metaTags, structuredData, linkTags]);
  
  return null;
};
